/*! Copyright (c) Safe As Milk. All rights reserved. */
import Animations from "animations";

// Persistent cache config — sort-by-untappd is expensive so we cache aggressively
// across page loads. Bump *_VERSION when changing on-disk shape to invalidate.
const CUSTOM_SORT_STORAGE_PREFIX = "hoppenaar:custom-sort:v2:";
const CUSTOM_SORT_CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour
const CUSTOM_SORT_MAX_BYTES = 3 * 1024 * 1024; // ~3MB, well under typical 5MB quota
const UNTAPPD_DATA_STORAGE_PREFIX = "hoppenaar:untappd-data:v1:";
const UNTAPPD_DATA_CACHE_TTL_MS = 6 * 60 * 60 * 1000; // 6 hours

class DynamicProductSearch extends HTMLElement {
    #boundLinksHandler;
    #boundLinksHandlerWithScroll;
    #boundPopStateHandler;
    constructor() {
        super();
        this.#boundLinksHandler = this.#linksHandler.bind(this);
        this.#boundLinksHandlerWithScroll = this.#linksHandlerWithScroll.bind(this);
        this.#boundPopStateHandler = this.#popStateListener.bind(this);
    }
    connectedCallback() {
        this.abortController = null;
        this.listeners = [];
        this.customSortCache ??= new Map;
        this.customSortRequestId ??= 0;
        this.untappdDataCache ??= new Map;
        this.soldOutCache ??= new Map;
        this.soldOutRequestId ??= 0;
        this.section = this.closest(".section");
        this.updateLoading();
        this.#syncCustomSortUI(window.location.search.slice(1));
        this.#setUpListeners(window.location.search.slice(1));
        window.addEventListener("popstate", this.#boundPopStateHandler);
        if (this.#supportsCustomSort() && this.#isCustomSortQuery(window.location.search.slice(1))) {
            this.#renderCustomSortPage(window.location.search.slice(1), {
                updateURL: false,
                scrollUp: false
            });
        } else {
            this.#applySoldOutLast(window.location.search.slice(1)).then((changed => {
                if (changed) this.#afterSoldOutRender(window.location.search.slice(1));
            })).catch((() => {}));
        }
    }
    disconnectedCallback() {
        this.#removeListeners();
        window.removeEventListener("popstate", this.#boundPopStateHandler);
    }
    renderError(error = window.theme.localize("ERROR_PRODUCTS")) {
        if (this.previousElementSibling && this.previousElementSibling.classList.contains("error")) {
            this.previousElementSibling.remove();
        }
        const message = typeof error === "string" ? error : error?.message || window.theme.localize("ERROR_PRODUCTS");
        const errorNode = document.createElement("div");
        errorNode.classList.add("error");
        errorNode.innerHTML = message;
        this.section.prepend(errorNode);
    }
    // ------------------------------------------------------------------------
    // Hoppenaar: uitverkochte producten altijd achteraan.
    // Shopify kent geen sortering 'beschikbaar eerst'. De server rendert per
    // pagina eerst de beschikbare kaarten; hier vullen we de pagina aan met de
    // juiste producten uit de lijsten 'op voorraad' (filter.v.availability=1)
    // en 'uitverkocht' (=0), zodat over alle pagina's heen uitverkochte
    // producten achteraan staan. Paginering en totalen blijven gelijk.
    // ------------------------------------------------------------------------
    async #applySoldOutLast(query = "") {
        const requestId = ++this.soldOutRequestId;
        const results = this.querySelector(".js-results");
        if (!results) return false;
        if (this.#isCustomSortQuery(query)) return false;
        const params = this.#getSearchParams(query);
        if (params.has("filter.v.availability")) return false;
        const total = Number.parseInt(results.dataset.totalCount || "", 10);
        const available = Number.parseInt(results.dataset.availableCount || "", 10);
        const pageSize = this.#getPageSize();
        if (!Number.isFinite(total) || !Number.isFinite(available) || pageSize <= 0) return false;
        const soldOut = total - available;
        if (soldOut <= 0 || available <= 0) return false;
        const page = this.#getCurrentPage(query);
        const start = (page - 1) * pageSize;
        const end = Math.min(start + pageSize, total);
        if (start >= total) return false;
        const cacheKey = `${this.#getCustomSortSignature(query)}|${page}|${total}|${available}`;
        let html = this.soldOutCache.get(cacheKey);
        if (!html) {
            const requests = [];
            const availableNeeded = Math.max(0, Math.min(end, available) - start);
            if (availableNeeded > 0) {
                requests.push(this.#fetchAvailabilityItems(params, "1", page).then((items => items.slice(0, availableNeeded))));
            }
            const soldStart = Math.max(start, available) - available;
            const soldEnd = end - available;
            if (soldEnd > soldStart) {
                // De uitverkocht-lijst kan producten met gemengde varianten bevatten
                // die ook in de beschikbaar-lijst staan; die filteren we eruit.
                requests.push(this.#collectSoldOutItems(params, soldEnd).then((items => items.slice(soldStart, soldEnd))));
            }
            const parts = await Promise.all(requests);
            const items = parts.flat();
            if (items.length !== end - start) {
                console.warn(`[sold-out-last] Onverwacht aantal (${items.length} i.p.v. ${end - start}) — volgorde niet aangepast`);
                return false;
            }
            html = items.join("");
            this.soldOutCache.set(cacheKey, html);
        }
        if (requestId !== this.soldOutRequestId) throw new DOMException("Sold-out request aborted", "AbortError");
        const currentIds = Array.from(results.querySelectorAll(":scope > .o-layout__item product-card[data-product-id]")).map((el => el.dataset.productId)).join(",");
        const wrapper = (new DOMParser).parseFromString(`<div id="hp-sold-out">${html}</div>`, "text/html").getElementById("hp-sold-out");
        const newIds = Array.from(wrapper.querySelectorAll(":scope > .o-layout__item product-card[data-product-id]")).map((el => el.dataset.productId)).join(",");
        if (currentIds === newIds) return false;
        this.#cleanupMovedQuickShops();
        results.innerHTML = html;
        return true;
    }
    async #collectSoldOutItems(params, needed) {
        const signature = this.#getCustomSortSignature(params.toString());
        this.soldOutPages ??= new Map;
        const collected = [];
        for (let page = 1; page <= 50 && collected.length < needed; page += 1) {
            const key = `${signature}|${page}`;
            let items = this.soldOutPages.get(key);
            if (!items) {
                items = await this.#fetchAvailabilityItems(params, "0", page);
                this.soldOutPages.set(key, items);
            }
            if (items.length === 0) break;
            items.forEach((html => {
                if (/^<div[^>]*data-sold-out="true"/.test(html)) collected.push(html);
            }));
        }
        return collected;
    }
    async #fetchAvailabilityItems(params, availability, page) {
        const p = new URLSearchParams(params.toString());
        p.delete("page");
        p.delete("custom_sort");
        p.delete("preview_theme_id");
        p.set("filter.v.availability", availability);
        if (page > 1) p.set("page", `${page}`);
        const responseText = await this.#fetchSectionPage(p.toString());
        const html = (new DOMParser).parseFromString(responseText, "text/html");
        const source = html.querySelector(`.section--${this.dataset.section} .js-results`);
        if (!source) return [];
        return Array.from(source.querySelectorAll(":scope > .o-layout__item")).filter((item => item.querySelector("product-card[data-product-id]"))).map((item => item.outerHTML));
    }
    #afterSoldOutRender(query = "") {
        Animations.setup(this.section);
        this.#removeListeners();
        this.#setUpListeners(query);
        if (this.dataset.quickShopDynamicCheckout === "true" && window.Shopify && window.Shopify.PaymentButton) window.Shopify.PaymentButton.init();
        this.dispatchEvent(new CustomEvent("on:dynamic-product-search:loaded"));
    }
    renderCollection(params) {
        const {query: query, updateURL: updateURL, scrollUp: scrollUp} = {
            query: "",
            updateURL: true,
            scrollUp: false,
            ...params
        };
        const normalizedQuery = this.#normalizeCustomSortQuery(this.#stripLeadingQuestionMark(query));
        const currentFocusElementId = document.activeElement && document.activeElement.getAttribute("id");
        const sectionClass = `.section--${this.dataset.section}`;
        this.customSortRequestId += 1;
        this.abortController = new AbortController;
        this.updateLoading(true);
        fetch(`${this.dataset.url}?${normalizedQuery}&section_id=${this.dataset.section}`, {
            signal: this.abortController.signal
        }).then((response => {
            if (response.ok) return response.text();
            throw new Error(window.theme.localize("ERROR_PRODUCTS"));
        })).then((async responseText => {
            this.dispatchEvent(new CustomEvent("on:dynamic-product-search:loading"));
            let sidebarContentOffset = 0;
            const predictiveSearch = this.querySelector("predictive-search");
            if (predictiveSearch) predictiveSearch.close();
            const openFiltersModal = this.querySelector('.modal--filters:not([aria-hidden="true"])');
            if (openFiltersModal) {
                const content = openFiltersModal.querySelector(".collection-sidebar__wrapper");
                if (content) {
                    sidebarContentOffset = content.scrollTop;
                }
            }
            const html = (new DOMParser).parseFromString(responseText, "text/html");
            const destination = document.querySelector(sectionClass);
            const source = html.querySelector(sectionClass);
            if (source && destination) {
                this.#removeListeners();
                const destinationMainContainer = destination.querySelector(".js-main-content");
                const sourceMainContainer = source.querySelector(".js-main-content");
                if (destinationMainContainer.children.length > 0 && sourceMainContainer.children.length > 0) {
                    const destinationResultsTotal = destination.querySelector(`.js-results-total`);
                    const destinationSortForm = destination.querySelector(`.js-sort-form`);
                    const destinationActiveFilters = destination.querySelector(`.js-active-filters`);
                    const destinationFilters = destination.querySelector(`.js-filters`);
                    const destinationActions = destination.querySelector(`.js-actions`);
                    const destinationResultsContainer = destination.querySelector(`.js-results-container`);
                    const destinationResults = destination.querySelector(`.js-results`);
                    const destinationPagination = destination.querySelector(`.js-pagination`);
                    const sourceResultsTotal = source.querySelector(`.js-results-total`);
                    const sourceSortForm = source.querySelector(`.js-sort-form`);
                    const sourceActiveFilters = source.querySelector(`.js-active-filters`);
                    const sourceFilters = source.querySelector(`.js-filters`);
                    const sourceActions = source.querySelector(`.js-actions`);
                    const sourceResultsContainer = source.querySelector(`.js-results-container`);
                    const sourceResults = source.querySelector(`.js-results`);
                    const sourcePagination = source.querySelector(`.js-pagination`);
                    if (destinationResultsTotal && sourceResultsTotal) {
                        destinationResultsTotal.innerHTML = sourceResultsTotal.innerHTML;
                    }
                    if (destinationResultsContainer && sourceResultsContainer) {
                        if (destinationSortForm && sourceSortForm) destinationSortForm.innerHTML = sourceSortForm.innerHTML;
                        if (destinationActiveFilters && sourceActiveFilters) destinationActiveFilters.innerHTML = sourceActiveFilters.innerHTML;
                        if (destinationFilters && sourceFilters) destinationFilters.innerHTML = sourceFilters.innerHTML;
                        if (destinationActions && sourceActions) destinationActions.innerHTML = sourceActions.innerHTML;
                        if (destinationResults && sourceResults) {
                            destinationResults.innerHTML = sourceResults.innerHTML;
                            [ "totalCount", "availableCount" ].forEach((key => {
                                if (sourceResults.dataset[key] !== undefined) {
                                    destinationResults.dataset[key] = sourceResults.dataset[key];
                                } else {
                                    delete destinationResults.dataset[key];
                                }
                            }));
                        }
                    } else if (destinationResultsContainer) {
                        destinationResultsContainer.remove();
                    } else {
                        destinationMainContainer.appendChild(sourceResultsContainer);
                    }
                    if (destinationPagination && sourcePagination) {
                        destinationPagination.innerHTML = sourcePagination.innerHTML;
                    } else if (destinationPagination) {
                        destinationPagination.remove();
                    } else if (sourcePagination) {
                        destination.querySelector(".js-main-content").appendChild(sourcePagination);
                    }
                } else {
                    const destinationSectionTitle = destination.querySelector(".js-section-title");
                    const sourceSectionTitle = source.querySelector(".js-section-title");
                    if (destinationSectionTitle && sourceSectionTitle) destinationSectionTitle.innerHTML = sourceSectionTitle.innerHTML;
                    if (destinationMainContainer && sourceMainContainer) destinationMainContainer.innerHTML = sourceMainContainer.innerHTML;
                }
                this.#syncCustomSortUI(normalizedQuery);
                if (this.#isCustomSortQuery(normalizedQuery)) {
                    await this.#applyCustomSortOnly(normalizedQuery);
                } else {
                    try {
                        await this.#applySoldOutLast(normalizedQuery);
                    } catch (error) {
                        if (error?.name === "AbortError") throw error;
                        console.warn("[sold-out-last] Herordenen mislukt", error);
                    }
                    this.#setUpListeners(normalizedQuery);
                }
            }
            Animations.setup(document.querySelector(sectionClass));
            if (openFiltersModal) {
                const content = openFiltersModal.querySelector(".collection-sidebar__wrapper");
                if (content && sidebarContentOffset !== 0) {
                    content.scrollTop = sidebarContentOffset;
                }
            }
            if (scrollUp) this.scrollUp();
            const newPageTitle = document.querySelector(`${sectionClass} dynamic-product-search`).dataset.pageTitle;
            if (updateURL) this.updateURL(normalizedQuery, newPageTitle);
            document.title = newPageTitle;
            this.updateLoading(false);
            if (this.dataset.quickShopDynamicCheckout === "true" && window.Shopify && window.Shopify.PaymentButton) window.Shopify.PaymentButton.init();
            const currentlyFocusedElement = document.getElementById(currentFocusElementId);
            if (currentlyFocusedElement) {
                currentlyFocusedElement.focus();
            }
            this.dispatchEvent(new CustomEvent("on:dynamic-product-search:loaded"));
        })).catch((async error => {
            if (error.name && error.name === "AbortError") return;
            this.updateLoading(false);
            this.renderError(error);
            const openFiltersModal = this.querySelector('.modal--filters:not([aria-hidden="true"])');
            if (openFiltersModal) await openFiltersModal.close();
            this.scrollUp();
            this.dispatchEvent(new CustomEvent("on:dynamic-product-search:failed"));
        }));
    }
    scrollUp() {
        const {top: top} = this.section.getBoundingClientRect();
        window.scrollBy({
            behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "instant" : "smooth",
            top: top
        });
    }
    updateLoading(isLoading = false) {
        this.loading = isLoading;
        if (isLoading) {
            this.section.classList.add("is-loading");
        } else {
            this.section.classList.remove("is-loading");
        }
    }
    updateURL(query, title = "") {
        if (this.dataset.updateUrl === "false") return;
        window.history.pushState({}, title, `${this.dataset.url}${query ? `?${query}` : ""}`);
    }
    async #renderCustomSortPage(query, params = {}) {
        const {updateURL: updateURL, scrollUp: scrollUp} = {
            updateURL: true,
            scrollUp: false,
            ...params
        };
        const normalizedQuery = this.#normalizeCustomSortQuery(this.#stripLeadingQuestionMark(query));
        if (!this.#supportsCustomSort() || !this.#isCustomSortQuery(normalizedQuery)) {
            this.renderCollection({
                query: normalizedQuery,
                updateURL: updateURL,
                scrollUp: scrollUp
            });
            return;
        }
        this.updateLoading(true);
        try {
            await this.#applyCustomSortOnly(normalizedQuery);
            Animations.setup(this.section);
            const newPageTitle = this.dataset.pageTitle || document.title;
            if (updateURL) this.updateURL(normalizedQuery, newPageTitle);
            document.title = newPageTitle;
            if (scrollUp) this.scrollUp();
            if (this.dataset.quickShopDynamicCheckout === "true" && window.Shopify && window.Shopify.PaymentButton) window.Shopify.PaymentButton.init();
            this.dispatchEvent(new CustomEvent("on:dynamic-product-search:loaded"));
        } catch (error) {
            if (error.name && error.name === "AbortError") return;
            this.renderError(error);
            this.dispatchEvent(new CustomEvent("on:dynamic-product-search:failed"));
        } finally {
            this.updateLoading(false);
        }
    }
    #popStateListener() {
        const query = window.location.search.slice(1);
        this.renderCollection({
            updateURL: false,
            query: query
        });
    }
    #linksHandler(e, scrollUp = false) {
        e.preventDefault();
        const trigger = e.currentTarget;
        if (!trigger.getAttribute("href") && !trigger.dataset.url) return;
        const url = trigger.getAttribute("href") || trigger.dataset.url;
        const query = this.#buildNavigationQuery(url);
        if (trigger.dataset.customPage === "true" && this.#isCustomSortQuery(query)) {
            this.#renderCustomSortPage(query, {
                scrollUp: scrollUp
            });
            return;
        }
        this.renderCollection({
            query: query,
            scrollUp: scrollUp
        });
    }
    #linksHandlerWithScroll(e) {
        this.#linksHandler(e, true);
    }
    #filtersControl() {
        const filters = this.querySelectorAll(".js-filter-trigger");
        filters.forEach((filter => {
            filter.addEventListener("click", this.#boundLinksHandler);
            this.listeners.push({
                element: filter,
                event: "click",
                handler: this.#boundLinksHandler
            });
        }));
    }
    #paginationControl() {
        const paginationLinks = this.querySelectorAll(".pagination a");
        paginationLinks.forEach((paginationLink => {
            paginationLink.addEventListener("click", this.#boundLinksHandlerWithScroll);
            this.listeners.push({
                element: paginationLink,
                event: "click",
                handler: this.#boundLinksHandlerWithScroll
            });
        }));
    }
    #setUpListeners(query = window.location.search.slice(1)) {
        this.forms = [ this.querySelector(".js-search-form"), this.querySelector(".js-sort-form"), this.querySelector(".js-filters-form") ].filter((form => form));
        if (this.querySelector(".js-search-form") && this.querySelector(".js-filters-form")) {
            const queryInput = this.querySelector('.js-filters-form input[name="q"]');
            if (queryInput) queryInput.remove();
        }
        this.forms.forEach((form => {
            if (form.dataset.submitOnChange === "true") {
                const changeHandler = () => {
                    form.requestSubmit();
                };
                form.addEventListener("change", changeHandler);
                this.listeners.push({
                    element: form,
                    event: "change",
                    handler: changeHandler
                });
            }
            const submitHandler = (e => {
                e.preventDefault();
                if (this.abortController) this.abortController.abort();
                const query = this.#buildFormQuery(e.currentTarget);
                this.renderCollection({
                    query: query
                });
            });
            form.addEventListener("submit", submitHandler);
            this.listeners.push({
                element: form,
                event: "submit",
                handler: submitHandler
            });
        }));
        this.#syncCustomSortUI(query);
        this.#filtersControl();
        this.#paginationControl();
    }
    #removeListeners() {
        this.listeners.forEach((({element: element, event: event, handler: handler}) => {
            element.removeEventListener(event, handler);
        }));
        this.listeners = [];
    }
    #stripLeadingQuestionMark(query = "") {
        return query.startsWith("?") ? query.slice(1) : query;
    }
    #getSearchParams(query = "") {
        return new URLSearchParams(this.#stripLeadingQuestionMark(query));
    }
    #normalizeCustomSortQuery(query = "") {
        if (!this.#supportsCustomSort()) return this.#stripLeadingQuestionMark(query);
        const params = this.#getSearchParams(query);
        if (params.get("sort_by") === this.dataset.customSortKey) {
            params.delete("sort_by");
            params.set("custom_sort", this.dataset.customSortKey);
        }
        return params.toString();
    }
    #supportsCustomSort() {
        return Boolean(this.dataset.customSortKey) && this.#getPageSize() > 0;
    }
    #getPageSize() {
        return Number.parseInt(this.dataset.pageSize || "", 10) || 0;
    }
    #isCustomSortQuery(query = "") {
        if (!this.#supportsCustomSort()) return false;
        const params = this.#getSearchParams(query);
        return params.get("custom_sort") === this.dataset.customSortKey || params.get("sort_by") === this.dataset.customSortKey;
    }
    #isCustomSortSelected() {
        if (!this.#supportsCustomSort()) return false;
        return Array.from(this.querySelectorAll(".js-collection-sort")).some((select => select.value === this.dataset.customSortKey));
    }
    #syncCustomSortUI(query = "") {
        if (!this.#supportsCustomSort()) return;
        const params = this.#getSearchParams(query);
        const customSortSelected = this.#isCustomSortQuery(query);
        const sortBy = customSortSelected ? null : params.get("sort_by");
        this.querySelectorAll(".js-collection-sort").forEach((select => {
            let customOption = Array.from(select.options).find((option => option.value === this.dataset.customSortKey));
            if (!customOption) {
                const option = document.createElement("option");
                option.value = this.dataset.customSortKey;
                option.textContent = this.dataset.customSortLabel || this.dataset.customSortKey;
                select.appendChild(option);
                customOption = option;
            }
            if (customSortSelected) {
                select.value = this.dataset.customSortKey;
                Array.from(select.options).forEach((option => {
                    option.selected = option.value === this.dataset.customSortKey;
                }));
            } else if (sortBy && Array.from(select.options).some((option => option.value === sortBy))) {
                select.value = sortBy;
                Array.from(select.options).forEach((option => {
                    option.selected = option.value === sortBy;
                }));
            } else {
                Array.from(select.options).forEach((option => {
                    option.selected = false;
                }));
                if (select.selectedIndex >= 0) {
                    select.options[select.selectedIndex].selected = true;
                }
            }
            if (customOption && !customSortSelected) customOption.selected = false;
        }));
    }
    #buildFormQuery(targetForm) {
        const params = this.#getSearchParams(this.#serializeForms(targetForm));
        if (!this.#supportsCustomSort()) return params.toString();
        if (this.#isCustomSortSelected()) {
            params.delete("sort_by");
            params.set("custom_sort", this.dataset.customSortKey);
            params.delete("page");
            return params.toString();
        }
        if (params.get("sort_by") === this.dataset.customSortKey) {
            params.delete("sort_by");
        }
        params.delete("custom_sort");
        return params.toString();
    }
    #serializeForms(targetForm) {
        let query = "";
        if (targetForm.classList.contains("js-search-form")) {
            const paramsToKeep = [ "type", "q", "sort_by" ];
            query = this.forms.reduce(((queryString, form) => {
                const formData = new FormData(form);
                for (const [paramName] of formData.entries()) {
                    if (!paramsToKeep.includes(paramName)) formData.delete(paramName);
                }
                const thisQueryString = new URLSearchParams(formData).toString();
                return `${queryString}${thisQueryString ? `${queryString ? "&" : ""}${thisQueryString}` : ""}`;
            }), "");
        } else {
            query = this.forms.reduce(((queryString, form) => {
                const formData = new FormData(form);
                const thisQueryString = new URLSearchParams(formData).toString();
                return `${queryString}${thisQueryString ? `${queryString ? "&" : ""}${thisQueryString}` : ""}`;
            }), "");
        }
        return query;
    }
    #buildNavigationQuery(url) {
        const query = this.#normalizeCustomSortQuery(url.split("?").length > 1 ? url.split("?")[1] : "");
        const params = this.#getSearchParams(query);
        if (!this.#supportsCustomSort()) return params.toString();
        const currentParams = this.#getSearchParams(window.location.search.slice(1));
        if (currentParams.get("custom_sort") === this.dataset.customSortKey && params.get("custom_sort") !== this.dataset.customSortKey) {
            params.set("custom_sort", this.dataset.customSortKey);
            params.delete("sort_by");
            params.delete("page");
        }
        return params.toString();
    }
    async #applyCustomSortOnly(query = "") {
        if (!this.#supportsCustomSort() || !this.#isCustomSortQuery(query)) return;
        const requestId = ++this.customSortRequestId;
        const cacheEntry = await this.#getCustomSortCacheEntry(query, requestId);
        this.#assertCustomSortRequestActive(requestId);
        this.#renderCustomSortResults(cacheEntry, query);
        this.#syncCustomSortUI(query);
        this.#removeListeners();
        this.#setUpListeners(query);
    }
    #assertCustomSortRequestActive(requestId) {
        if (requestId !== this.customSortRequestId) {
            throw new DOMException("Custom sort request aborted", "AbortError");
        }
    }
    #getCustomSortSignature(query = "") {
        const params = this.#getSearchParams(query);
        params.delete("page");
        params.delete("custom_sort");
        if (params.get("sort_by") === this.dataset.customSortKey) params.delete("sort_by");
        return params.toString();
    }
    #buildServerQuery(query = "", page = 1) {
        const params = this.#getSearchParams(query);
        params.delete("custom_sort");
        if (params.get("sort_by") === this.dataset.customSortKey) params.delete("sort_by");
        if (page > 1) {
            params.set("page", `${page}`);
        } else {
            params.delete("page");
        }
        return params.toString();
    }
    async #getCustomSortCacheEntry(query = "", requestId) {
        const signature = this.#getCustomSortSignature(query);
        const cached = this.customSortCache.get(signature);
        if (cached) return cached;
        const persisted = this.#readPersistentSortCache(signature);
        if (persisted) {
            this.customSortCache.set(signature, persisted);
            return persisted;
        }
        const items = [];
        const seenProductIds = new Set;
        // Fetch page 1 first to learn how many pages exist, then parallelize the rest.
        this.#assertCustomSortRequestActive(requestId);
        const firstResponseText = await this.#fetchSectionPage(this.#buildServerQuery(query, 1));
        this.#assertCustomSortRequestActive(requestId);
        const firstHtml = (new DOMParser).parseFromString(firstResponseText, "text/html");
        const firstSource = firstHtml.querySelector(`.section--${this.dataset.section}`);
        this.#collectCustomSortItems(firstSource, items, seenProductIds);
        const totalPages = this.#determineTotalPages(firstSource);
        if (totalPages > 1) {
            const remainingPageNumbers = [];
            for (let page = 2; page <= totalPages; page += 1) {
                remainingPageNumbers.push(page);
            }
            const remainingResponses = await Promise.all(remainingPageNumbers.map((page => this.#fetchSectionPage(this.#buildServerQuery(query, page)))));
            this.#assertCustomSortRequestActive(requestId);
            for (const responseText of remainingResponses) {
                const html = (new DOMParser).parseFromString(responseText, "text/html");
                const source = html.querySelector(`.section--${this.dataset.section}`);
                this.#collectCustomSortItems(source, items, seenProductIds);
            }
        }
        await this.#hydrateUntappdData(items, requestId);
        this.#assertCustomSortRequestActive(requestId);
        const sortedItems = this.#sortCustomSortItems(items);
        const cacheEntry = {
            items: sortedItems,
            totalPages: sortedItems.length > 0 ? Math.ceil(sortedItems.length / this.#getPageSize()) : 0
        };
        this.customSortCache.set(signature, cacheEntry);
        this.#writePersistentSortCache(signature, cacheEntry);
        return cacheEntry;
    }
    #determineTotalPages(source) {
        if (!source) return 1;
        const pageSize = this.#getPageSize();
        const totalNode = source.querySelector(".js-results-total");
        if (totalNode && pageSize > 0) {
            const text = totalNode.textContent || "";
            const match = text.replace(/[\s.,]/g, "").match(/\d+/);
            if (match) {
                const total = Number.parseInt(match[0], 10);
                if (Number.isFinite(total) && total > 0) {
                    return Math.max(1, Math.ceil(total / pageSize));
                }
            }
        }
        // Fallback: scan pagination links for the highest ?page=N value
        let maxPage = 1;
        source.querySelectorAll(".pagination a[href]").forEach((link => {
            const href = link.getAttribute("href") || "";
            const m = href.match(/[?&]page=(\d+)/);
            if (m) {
                const p = Number.parseInt(m[1], 10);
                if (Number.isFinite(p) && p > maxPage) maxPage = p;
            }
        }));
        return maxPage;
    }
    async #fetchSectionPage(query = "") {
        const response = await fetch(`${this.dataset.url}?${query}&section_id=${this.dataset.section}`);
        if (!response.ok) {
            throw new Error(window.theme.localize("ERROR_PRODUCTS"));
        }
        return response.text();
    }
    #collectCustomSortItems(source, items, seenProductIds) {
        const results = source?.querySelector(".js-results");
        if (!results) return;
        results.querySelectorAll(".o-layout__item").forEach((item => {
            const productCard = item.querySelector("product-card[data-product-id]");
            if (!productCard) return;
            const productId = productCard.dataset.productId;
            if (!productId || seenProductIds.has(productId)) return;
            seenProductIds.add(productId);
            items.push({
                html: item.outerHTML,
                originalIndex: items.length,
                productId: productId,
                soldOut: item.dataset.soldOut === "true",
                rating: this.#parseUntappdRating(productCard.dataset.untappdRating),
                ratingCount: this.#parseUntappdCount(productCard.dataset.untappdRatingCount),
                untappdUrl: productCard.dataset.untappdUrl || ""
            });
        }));
    }
    async #hydrateUntappdData(items, requestId) {
        const itemsNeedingUntappdData = items.filter((item => item.rating == null && item.untappdUrl));
        if (itemsNeedingUntappdData.length === 0) return;
        await Promise.all(itemsNeedingUntappdData.map((async item => {
            this.#assertCustomSortRequestActive(requestId);
            const untappdData = await this.#getUntappdData(item.untappdUrl);
            this.#assertCustomSortRequestActive(requestId);
            if (!untappdData) return;
            item.rating = this.#parseUntappdRating(untappdData.rating);
            item.ratingCount = this.#parseUntappdCount(untappdData.count ?? untappdData.rating_count ?? untappdData.reviews);
        })));
    }
    async #getUntappdData(untappdUrl = "") {
        if (!untappdUrl) return null;
        if (this.untappdDataCache.has(untappdUrl)) {
            return this.untappdDataCache.get(untappdUrl);
        }
        const persisted = this.#readPersistentUntappdData(untappdUrl);
        if (persisted) {
            const resolved = Promise.resolve(persisted);
            this.untappdDataCache.set(untappdUrl, resolved);
            return resolved;
        }
        const request = fetch(`https://hoppenaar-untappd.colin-43f.workers.dev/untappd?url=${encodeURIComponent(untappdUrl)}`).then((response => {
            if (!response.ok) return null;
            return response.json();
        })).then((data => {
            if (data) this.#writePersistentUntappdData(untappdUrl, data);
            return data;
        })).catch((() => null));
        this.untappdDataCache.set(untappdUrl, request);
        return request;
    }
    #parseUntappdRating(value) {
        if (value == null || value === "") return null;
        const parsedValue = Number.parseFloat(value);
        if (!Number.isFinite(parsedValue) || parsedValue <= 0) return null;
        return parsedValue;
    }
    #parseUntappdCount(value) {
        if (value == null || value === "") return null;
        const parsedValue = Number.parseInt(value, 10);
        if (!Number.isFinite(parsedValue) || parsedValue < 0) return null;
        return parsedValue;
    }
    #sortCustomSortItems(items) {
        return [ ...items ].sort(((a, b) => {
            if (Boolean(a.soldOut) !== Boolean(b.soldOut)) return a.soldOut ? 1 : -1;
            const ratingA = a.rating ?? -1;
            const ratingB = b.rating ?? -1;
            if (ratingA !== ratingB) return ratingB - ratingA;
            const ratingCountA = a.ratingCount ?? -1;
            const ratingCountB = b.ratingCount ?? -1;
            if (ratingCountA !== ratingCountB) return ratingCountB - ratingCountA;
            return a.originalIndex - b.originalIndex;
        }));
    }
    #renderCustomSortResults(cacheEntry, query = "") {
        const results = this.querySelector(".js-results");
        if (!results) return;
        const pagination = this.querySelector(".js-pagination");
        this.#cleanupMovedQuickShops();
        if (cacheEntry.totalPages === 0) {
            if (pagination) pagination.innerHTML = "";
            return;
        }
        const currentPage = Math.min(this.#getCurrentPage(query), cacheEntry.totalPages);
        const pageSize = this.#getPageSize();
        const start = (currentPage - 1) * pageSize;
        const end = start + pageSize;
        results.innerHTML = cacheEntry.items.slice(start, end).map((item => item.html)).join("");
        if (pagination) {
            pagination.innerHTML = this.#renderCustomPaginationMarkup(query, currentPage, cacheEntry.totalPages);
        }
    }
    #cleanupMovedQuickShops() {
        const movedModalElements = document.getElementById("moved-modal-elements");
        if (!movedModalElements) return;
        movedModalElements.querySelectorAll("quick-shop").forEach((quickShop => {
            quickShop.remove();
        }));
    }
    #getCurrentPage(query = "") {
        const page = Number.parseInt(this.#getSearchParams(query).get("page") || "1", 10);
        if (!Number.isFinite(page) || page < 1) return 1;
        return page;
    }
    #renderCustomPaginationMarkup(query, currentPage, totalPages) {
        if (totalPages <= 1) return "";
        const paginationParts = [];
        if (currentPage > 1) {
            paginationParts.push(`
        <div class="prev">
          <a href="${this.#buildCustomPaginationUrl(query, currentPage - 1)}" data-custom-page="true">
            <span aria-hidden="true">&#8249;</span>
            <span class="u-hidden-visually">Previous Page</span>
          </a>
        </div>`);
        }
        this.#getVisiblePaginationPages(totalPages, currentPage).forEach((page => {
            if (page == null) {
                paginationParts.push('<div class="deco" aria-hidden="true">...</div>');
                return;
            }
            if (page === currentPage) {
                paginationParts.push(`
          <div class="page current" aria-current="page">
            <span>${page}</span>
          </div>`);
                return;
            }
            paginationParts.push(`
          <div class="page">
            <a href="${this.#buildCustomPaginationUrl(query, page)}" data-custom-page="true">${page}</a>
          </div>`);
        }));
        if (currentPage < totalPages) {
            paginationParts.push(`
        <div class="next">
          <a href="${this.#buildCustomPaginationUrl(query, currentPage + 1)}" data-custom-page="true">
            <span aria-hidden="true">&#8250;</span>
            <span class="u-hidden-visually">Next Page</span>
          </a>
        </div>`);
        }
        return `
      <div class="container">
        <div class="pagination">
          <div class="pagination__items">
            ${paginationParts.join("")}
          </div>
        </div>
      </div>`;
    }
    #buildCustomPaginationUrl(query = "", page = 1) {
        const params = this.#getSearchParams(query);
        if (page > 1) {
            params.set("page", `${page}`);
        } else {
            params.delete("page");
        }
        return `${this.dataset.url}?${params.toString()}`;
    }
    #getPersistentSortStorageKey(signature) {
        const sectionId = this.dataset.section || "";
        const sortKey = this.dataset.customSortKey || "";
        const url = this.dataset.url || "";
        return `${CUSTOM_SORT_STORAGE_PREFIX}${sectionId}|${sortKey}|${url}|${signature}`;
    }
    #readPersistentSortCache(signature) {
        try {
            const raw = window.localStorage.getItem(this.#getPersistentSortStorageKey(signature));
            if (!raw) return null;
            const data = JSON.parse(raw);
            if (!data || typeof data !== "object") return null;
            if (!Number.isFinite(data.savedAt) || Date.now() - data.savedAt > CUSTOM_SORT_CACHE_TTL_MS) return null;
            if (!Array.isArray(data.items)) return null;
            return {
                items: data.items,
                totalPages: Number.isFinite(data.totalPages) ? data.totalPages : (data.items.length > 0 ? Math.ceil(data.items.length / this.#getPageSize()) : 0)
            };
        } catch (e) {
            return null;
        }
    }
    #writePersistentSortCache(signature, entry) {
        try {
            const payload = JSON.stringify({
                savedAt: Date.now(),
                totalPages: entry.totalPages,
                items: entry.items
            });
            if (payload.length > CUSTOM_SORT_MAX_BYTES) return;
            window.localStorage.setItem(this.#getPersistentSortStorageKey(signature), payload);
        } catch (e) {
            // Quota exceeded, private mode, or storage disabled — silently skip persisting.
        }
    }
    #getPersistentUntappdStorageKey(untappdUrl) {
        return `${UNTAPPD_DATA_STORAGE_PREFIX}${untappdUrl}`;
    }
    #readPersistentUntappdData(untappdUrl) {
        try {
            const raw = window.localStorage.getItem(this.#getPersistentUntappdStorageKey(untappdUrl));
            if (!raw) return null;
            const data = JSON.parse(raw);
            if (!data || typeof data !== "object") return null;
            if (!Number.isFinite(data.savedAt) || Date.now() - data.savedAt > UNTAPPD_DATA_CACHE_TTL_MS) return null;
            return data.payload || null;
        } catch (e) {
            return null;
        }
    }
    #writePersistentUntappdData(untappdUrl, payload) {
        try {
            window.localStorage.setItem(this.#getPersistentUntappdStorageKey(untappdUrl), JSON.stringify({
                savedAt: Date.now(),
                payload: payload
            }));
        } catch (e) {
            // Quota exceeded, private mode, or storage disabled — silently skip persisting.
        }
    }
    #getVisiblePaginationPages(totalPages, currentPage) {
        if (totalPages <= 7) {
            return Array.from({
                length: totalPages
            }, ((_, index) => index + 1));
        }
        const pages = new Set([ 1, totalPages, currentPage, currentPage - 1, currentPage + 1 ]);
        if (currentPage <= 3) {
            pages.add(2);
            pages.add(3);
            pages.add(4);
        }
        if (currentPage >= totalPages - 2) {
            pages.add(totalPages - 1);
            pages.add(totalPages - 2);
            pages.add(totalPages - 3);
        }
        const sortedPages = [ ...pages ].filter((page => page >= 1 && page <= totalPages)).sort(((a, b) => a - b));
        return sortedPages.reduce(((visiblePages, page, index) => {
            if (index > 0 && page - sortedPages[index - 1] > 1) visiblePages.push(null);
            visiblePages.push(page);
            return visiblePages;
        }), []);
    }
}

customElements.define("dynamic-product-search", DynamicProductSearch);
//# sourceMappingURL=dynamic-product-search.js.map
